/**
 * The Client Partner, and the four things every screen has to do with one.
 *
 * NOS DOES NOT STORE THIS. There is no owner column on Account and none on
 * Demand; what the warehouse holds is the Client Partner role and the hours
 * those people book, and the service infers ownership from where that time
 * goes. Everything here exists to keep that visible: the name is evidence, not
 * a record, and a screen that renders it as a bare string turns an inference
 * into a fact the moment someone screenshots it.
 *
 * Four jobs, one module, because the same three mistakes are available at every
 * call site:
 *
 *  1. "No Client Partner" is a REAL and common answer -- 15 of 116 revenue-
 *     bearing accounts have one -- and it must read as an answer. A blank cell
 *     reads as a loading bug and a dash reads as "not applicable", which is a
 *     different claim.
 *  2. A CONTESTED owner is a single name that is not settled, and a name shown
 *     without that qualification is worse than no name at all.
 *  3. An INHERITED owner on a project is the account's partner, not the
 *     project's, and 2,581 of 5,218 project rows are in that state. It is
 *     normal, so it is said quietly -- but it is said.
 */
import type { FilterDef } from "@/components/profitability/filters";
import type { ClientPartner, ProjectRow } from "@/lib/profitability-api";

/**
 * What the screens call an account or project no Client Partner is inferred for.
 *
 * A word rather than a dash. The accounts list already spends its dashes on
 * "we cannot compute this" (a null margin, an account with no booked hours),
 * and an unowned account is not that -- it is a definite finding: nobody with
 * the Client Partner role has booked time here.
 */
export const UNASSIGNED = "Unassigned";

/** Why a row says Unassigned, for the cell's title. */
export const UNASSIGNED_HELP = "No Client Partner is recorded against this account in NOS.";

/**
 * The name to show, sort on and filter by.
 *
 * `null` and `undefined` collapse to the same answer on purpose. The service
 * sends `client_partner: null` for an account nobody has booked against; a row
 * missing the key entirely is an older payload. Neither one knows an owner, and
 * inventing a distinction between them on screen would be a distinction about
 * our deploy state, not about the account.
 */
export function clientPartnerLabel(owner: ClientPartner | null | undefined): string {
  return owner?.person_name ?? UNASSIGNED;
}

/**
 * The evidence behind the name, for the title on the cell.
 *
 * Every clause is a fact off the row -- whose hours, which kind, what share --
 * followed by the one sentence that stops the whole thing being read as a
 * record. That sentence is not decoration: this column will be screenshotted
 * into Slack beside a margin figure that IS a record.
 */
export function clientPartnerEvidence(owner: ClientPartner): string {
  const parts = [
    `Inferred, not stored: NOS has no owner field.`,
    `${owner.person_name} is recorded as the Client Partner on this ${
      owner.source === "demand_person" ? "project" : "account"
    } in NOS${owner.accountable ? ", accountable" : owner.responsible ? ", responsible" : ""}.`,
  ];
  if (owner.inherited_from_account) {
    parts.push(
      "Shown from the account: no Client Partner booked time to this project itself, which is the normal case.",
    );
  }
  return parts.join(" ");
}

/**
 * The rival partners behind a contested name, or null when it is settled.
 *
 * Named rather than merely flagged. "Contested" alone tells a reader the name
 * is doubtful and gives them nothing to do about it; the runners-up and their
 * hours are what turn it into a question someone can actually answer.
 */
export function clientPartnerRivals(owner: ClientPartner): string | null {
  if (!owner.contested) return null;
  // Named, because "contested" alone tells a reader the answer is doubtful and
  // gives them nothing to do about it. These are the other people NOS records
  // as Client Partner on this account, none of whom is marked accountable or
  // responsible -- which is precisely why no single name can be chosen.
  const runnersUp = owner.others.map((other) => other.person_name).join(", ");
  const tail = runnersUp ? ` Runners-up: ${runnersUp}.` : "";
  return `Contested: more than one Client Partner is recorded on this account and neither is marked accountable or responsible, so ${owner.person_name} is not a settled answer.${tail}`;
}

/** Why a project shows the account's partner rather than its own. */
export const INHERITED_HELP =
  "No Client Partner booked time to this project itself, so the account's is shown. That is the normal case -- a partner carries the account, not every demand under it.";

/**
 * The project's own Client Partner on the detail page, own or inherited.
 *
 * Read off one period row rather than any()'d across them, for the reason the
 * service itself gives for `is_passthrough`: ownership is a lifetime property
 * of the demand, so every period row carries the same answer, and reading one
 * makes that invariant visible at the call site instead of hiding it behind a
 * scan that would quietly paper over a disagreement.
 */
export function projectClientPartner(
  periods: readonly ProjectRow[] | undefined,
): ClientPartner | null {
  const last = periods?.[periods.length - 1];
  return last?.client_partner ?? null;
}

/**
 * The filter definition for "show me only my accounts".
 *
 * A factory rather than a constant because `FilterDef` is generic over the row
 * and the two lists carry different ones. Each page holds the array it builds
 * at MODULE level -- the filter hook and the filtering memo both take it as a
 * dependency, and a fresh array every render would rebuild the options on
 * every keystroke.
 *
 * `valueOf` returns the word UNASSIGNED rather than null for an unowned row,
 * which is the one place this filter deliberately departs from the others.
 * filters.ts treats null as "carries no value": such rows are never offered as
 * an option and never survive an active filter. For a month or a person's name
 * that is right. Here it would hide the 15 accounts nobody owns from the one
 * control built to find accounts -- and "which accounts has no partner touched"
 * is a question worth being able to ask.
 */
export function clientPartnerFilter<
  Row extends { client_partner?: ClientPartner | null },
>(): FilterDef<Row> {
  return {
    key: "client_partner",
    label: "Client Partner",
    valueOf: (row) => clientPartnerLabel(row.client_partner),
    help: "Who carries the account, from the Client Partner recorded against it in NOS (AccountPerson). Unassigned collects accounts with no Client Partner on record.",
  };
}
